import React, { useEffect, useMemo, useState } from "react";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import axios from "axios";

const ProductionMetricsMeasuresSummary = ({
  projectItemId,
  productionJobId,
  refreshKey,
  t,
  title,
  emptyMessage,
  showProjectItemDetails = true,
  onProjectItemLoaded
}) => {
  const [loading, setLoading] = useState(false);
  const [projectItem, setProjectItem] = useState(null);
  const [rows, setRows] = useState([]);

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("userToken"),
        "App-Network": localStorage.getItem("networkId")
      }
    });
  };

  useEffect(() => {
    if (!projectItemId && !productionJobId) {
      setProjectItem(null);
      onProjectItemLoaded?.(null);
      setRows([]);
      return;
    }

    setLoading(true);

    const endpoint = productionJobId
      ? `/api/v2/production-job/${productionJobId}/technology-metrics-measures`
      : `/api/v2/project-item/${projectItemId}/technology-metrics-measures`;

    apiCall("get", endpoint)
      .then((response) => {
        const payload = response?.data?.data || {};

        const loadedProjectItem = payload.projectItem || null;

        setProjectItem(loadedProjectItem);
        onProjectItemLoaded?.(loadedProjectItem);
        setRows(Array.isArray(payload.items) ? payload.items : []);
      })
      .catch((error) => {
        window.App.toastShow(
          "error",
          t ? t("error") : "Hiba",
          error.toString(),
          "",
          error.response
        );

        setProjectItem(null);
        onProjectItemLoaded?.(null);
        setRows([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectItemId, productionJobId, refreshKey, onProjectItemLoaded]);

  const getTechnologyName = (item) => {
    return item?.technology?.name || "Ismeretlen technológia";
  };

  const getMetricsName = (measureRow) => {
    return (
      measureRow?.metricsMeasure?.metrics?.name ||
      measureRow?.metricsMeasure?.metrics?.pid ||
      "Egyéb adatok"
    );
  };

  const getMeasureName = (measureRow) => {
    return (
      measureRow?.metricsMeasure?.name ||
      "-"
    );
  };

  const getMeasureValue = (measureRow) => {
    const value =
      measureRow?.aggregatedValue ??
      measureRow?.raw_value ??
      measureRow?.raw_string ??
      measureRow?.raw_int ??
      measureRow?.raw_decimal ??
      null;

    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const unitName = measureRow?.metricsMeasure?.unit?.name || "";
    const numericValue =
      typeof value === "number"
        ? value
        : typeof value === "string" && /^-?\d+(?:[.,]\d+)?$/.test(value.trim())
          ? Number(value.replace(",", "."))
          : null;
    const displayValue =
      numericValue !== null && Number.isFinite(numericValue)
        ? numericValue.toLocaleString(undefined, {
            maximumFractionDigits: 2
          })
        : value;

    return `${displayValue}${unitName ? ` ${unitName}` : ""}`;
  };

  const groupedItems = useMemo(() => {
    return rows
      .map((item) => {
        const technologyName = getTechnologyName(item);
        const metricsGroups = {};

        (item?.metricsMeasures || []).forEach((measureRow) => {
          const measureName = getMeasureName(measureRow);
          const value = getMeasureValue(measureRow);

          if (measureName === "-" && value === "-") {
            return;
          }

          const metricsName = getMetricsName(measureRow);

          if (!metricsGroups[metricsName]) {
            metricsGroups[metricsName] = [];
          }

          metricsGroups[metricsName].push(measureRow);
        });

        return {
          technologyName,
          metricsGroups
        };
      })
      .filter((item) => Object.keys(item.metricsGroups).length > 0);
  }, [rows]);

  if (!projectItemId && !productionJobId) {
    return null;
  }

  return (
    <div className="production-metrics-summary p-mt-4">
      <h4 className="production-metrics-summary-title p-mt-0 p-mb-3">
        {title || (t ? t("aggregatedProductionData") : "Összesített termelési adatok")}
      </h4>

      {showProjectItemDetails && projectItem && (
        <div className="p-mb-3" style={{ opacity: 0.75 }}>
          <div>
            <strong>{t ? t("fieldProjectItem") : "Projekt tétel"}:</strong>{" "}
            {projectItem.name || "-"}
          </div>
          <div>
            <strong>{t ? t("quantity") : "Mennyiség"}:</strong>{" "}
            {projectItem.quantity || 0} {projectItem.unit?.name || ""}
          </div>
        </div>
      )}

      {loading && (
        <ProgressBar mode="indeterminate" style={{ height: "5px" }} />
      )}

      {!loading && rows.length === 0 && (
        <Message
          severity="info"
          text={emptyMessage || (t
            ? t("noAggregatedProductionData")
            : "Ehhez még nincs rögzített összesített termelési adat.")}
        />
      )}

      {!loading && rows.length > 0 && groupedItems.map((groupedItem) => (
        <div key={groupedItem.technologyName} className="production-metrics-summary-technology p-mb-4">
          <div className="production-metrics-summary-technology-title p-mb-2">
            {groupedItem.technologyName}
          </div>

          {Object.keys(groupedItem.metricsGroups).map((metricsName) => (
            <div key={`${groupedItem.technologyName}-${metricsName}`} className="p-mb-3">
              <div className="production-metrics-summary-group-title p-mb-2">
                {metricsName}
              </div>

              <table
                className="production-metrics-summary-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1px solid var(--surface-d)"
                }}
              >
                <tbody>
                  {groupedItem.metricsGroups[metricsName].map((row, index) => (
                    <tr key={row?.id || `${groupedItem.technologyName}-${metricsName}-${index}`}>
                      <td
                        style={{
                          width: "50%",
                          padding: "10px",
                          border: "1px solid var(--surface-d)",
                          fontWeight: 600,
                          background: "var(--surface-b)"
                        }}
                      >
                        {getMeasureName(row)}
                      </td>

                      <td
                        style={{
                          padding: "10px",
                          border: "1px solid var(--surface-d)"
                        }}
                      >
                        {getMeasureValue(row)}
                      </td>
                                          </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ProductionMetricsMeasuresSummary;
